/**
 * sanitize-email-html — turn an untrusted email HTML body into safe markup for
 * the reading pane (Fix C).
 *
 * Email HTML is hostile input. Before it ever reaches the DOM we strip scripts,
 * event handlers, and remote-object embeds, and allow only basic formatting +
 * links. Links are forced to open in a new tab with rel="noopener noreferrer" so
 * a crafted target can neither reach window.opener nor leak the referrer.
 *
 * Never render an email body without passing it through here first — the result is
 * the ONLY string that may go into dangerouslySetInnerHTML.
 */
import DOMPurify from 'dompurify';

// Force every surviving anchor to open safely in a new tab. Registered once at
// module load; it only touches <a> nodes and there is no other DOMPurify consumer.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'A' && node.getAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/** Tags that must never survive, even though some are otherwise "structural". */
const FORBID_TAGS = [
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'link',
  'meta',
  'base',
];

/**
 * Sanitize an email HTML body to a safe string.
 * DOMPurify already removes on* handlers and javascript:/data: script URIs by
 * default; the FORBID_TAGS list additionally drops remote-object embeds and forms.
 */
export function sanitizeEmailHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    FORBID_TAGS,
    ADD_ATTR: ['target', 'rel'],
    ALLOW_DATA_ATTR: false,
    // Inline email images (B1): the api rewrites `cid:` refs to data URIs, which
    // DOMPurify strips by default. Scoped to <img> ALONE and paired with the
    // api-side MIME allowlist that excludes SVG, so no data: URI can carry markup.
    ADD_DATA_URI_TAGS: ['img'],
  });
}
